/**
 * Clinical Templates — the review-of-systems and physical-exam dropdowns.
 *
 * These are CLINICAL templates a physician edits at the point of care, not
 * generated text. This page is where a practice changes what those dropdowns
 * offer: the label, the findings on offer, which one is selected by default, the
 * order, and whether the system appears at all.
 *
 * NOTHING READS THESE ROWS YET. `member_360.visit_note_clinical_template` is
 * read by exactly one code path — this console's own resolver. No frontend
 * queries `visitNoteClinicalTemplates`, and no host passes `@astrana/ui`'s
 * `VisitNoteDrafter` `clinicalTemplates` override prop, so a physician's ROS/PE
 * dropdowns always come from the library's hardcoded defaults (which migration
 * 0018 was seeded FROM). There is no fallback relationship, because there is no
 * read: an edit saved here does not reach a note today. That is why every piece
 * of copy on this page has to say so rather than imply visibility — see the
 * banner below. Delete the banner when the drafter is wired to this table, and
 * not before.
 *
 * WHAT AN EMPTY TABLE MEANS: the same as a populated one, for now — the library
 * defaults are used either way. So an unseeded table costs a practice its
 * customisation, never the exam itself.
 *
 * GraphQL operations (resolvers live in care-management-gql):
 *   Query:    AcVisitNoteClinicalTemplates    → visitNoteClinicalTemplates[]
 *   Mutation: AcSetVisitNoteClinicalTemplate  → setVisitNoteClinicalTemplate
 */
import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import LoadingSpinner from "../../components/LoadingSpinner";
import { AdminWall } from "../components/AdminWall";
import {
  SET_VISIT_NOTE_CLINICAL_TEMPLATE_MUTATION,
  VISIT_NOTE_CLINICAL_TEMPLATES_QUERY,
  type SetVisitNoteClinicalTemplateResult,
  type SetVisitNoteClinicalTemplateVars,
  type VisitNoteClinicalTemplatesResult,
  type VisitNoteClinicalTemplateSystem,
} from "../graphql/adminOps";

const AXES = [
  { key: "ros", title: "Review of systems" },
  { key: "pe", title: "Physical exam" },
] as const;

/**
 * One editable system. Local state until Save, so a stray keystroke costs
 * nothing.
 *
 * COLLAPSED BY DEFAULT, AND THE BODY STAYS MOUNTED. Every system carries a
 * label field, a multi-line findings textarea and a default picker; 24 of them
 * expanded is a page nobody can navigate (and it pushed Physical exam so far
 * below the fold it read as missing). The body is hidden with the `hidden`
 * attribute rather than unmounted so that in-progress, unsaved edits survive a
 * collapse — the header surfaces them as "Unsaved" instead of silently
 * discarding them.
 */
function SystemRow({
  system,
  onSave,
  saving,
  open,
  onToggle,
}: {
  system: VisitNoteClinicalTemplateSystem;
  onSave: (next: VisitNoteClinicalTemplateSystem) => void;
  saving: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const [label, setLabel] = useState(system.label);
  const [optionsText, setOptionsText] = useState(system.options.join("\n"));
  const [defaultFinding, setDefaultFinding] = useState(system.defaultFinding);
  const [isActive, setIsActive] = useState(system.isActive);

  const options = optionsText
    .split("\n")
    .map((o) => o.trim())
    .filter(Boolean);

  // The same rule the backend and the table's CHECK enforce, surfaced BEFORE
  // the save: a default the dropdown cannot reproduce is a template the
  // physician cannot correct.
  const defaultIsOffered = options.includes(defaultFinding.trim());
  const problem = !options.length
    ? "A system must offer at least one finding."
    : !defaultIsOffered
      ? "The default must be one of the findings below."
      : null;

  // Compare NORMALIZED against NORMALIZED. The save sends `options` (trimmed,
  // blanks dropped) and `label.trim()`, so comparing the raw textarea against
  // the server's values left an ordinary edit — add a finding, press Enter —
  // permanently "Unsaved" after a *successful* save, which reads as "my edit did
  // not save".
  const dirty =
    label.trim() !== system.label ||
    defaultFinding.trim() !== system.defaultFinding ||
    isActive !== system.isActive ||
    options.join("\n") !== system.options.join("\n");

  const bodyId = `system-body-${system.id}`;

  return (
    // The testid carries the AXIS as well as the key: the table is
    // UNIQUE (axis, system_key), so cardiovascular / respiratory / neurological /
    // psychiatric each exist twice and a key-only testid is duplicated.
    <div
      className="rounded-lg border border-slate-200"
      data-testid={`system-${system.axis}-${system.systemKey}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
      >
        {open ? (
          <ChevronDown size={14} className="shrink-0 text-slate-500" />
        ) : (
          <ChevronRight size={14} className="shrink-0 text-slate-500" />
        )}
        <span className="text-sm font-medium text-slate-800">{label || system.systemKey}</span>
        <span className="font-mono text-[10px] text-slate-400">{system.systemKey}</span>
        <span className="ml-auto flex items-center gap-2">
          {dirty && <span className="text-[10px] font-medium text-amber-700">Unsaved</span>}
          {!isActive && (
            <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
              Disabled
            </span>
          )}
          <span className="text-[10px] text-slate-500">
            {options.length} finding{options.length === 1 ? "" : "s"}
          </span>
        </span>
      </button>

      <div id={bodyId} hidden={!open} className="border-t border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            aria-label={`Label for ${system.systemKey}`}
            className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm font-medium"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <label className="flex items-center gap-1 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={isActive}
              aria-label={`Enable ${system.systemKey}`}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Enabled
          </label>
        </div>

        <label className="mt-3 block text-xs font-medium text-slate-600">
          Findings — one per line
          <textarea
            aria-label={`Findings for ${system.systemKey}`}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
            rows={Math.max(3, options.length + 1)}
            value={optionsText}
            onChange={(e) => setOptionsText(e.target.value)}
          />
        </label>

        <label className="mt-2 block text-xs font-medium text-slate-600">
          Default
          <select
            aria-label={`Default for ${system.systemKey}`}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
            value={defaultIsOffered ? defaultFinding : ""}
            onChange={(e) => setDefaultFinding(e.target.value)}
          >
            {!defaultIsOffered && <option value="">Select a default…</option>}
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        {problem && <p className="mt-2 text-xs text-red-600">{problem}</p>}

        <button
          type="button"
          className="mt-3 rounded bg-slate-900 px-3 py-1 text-xs font-medium text-white disabled:opacity-40"
          disabled={!dirty || Boolean(problem) || saving}
          onClick={() =>
            onSave({
              ...system,
              label: label.trim(),
              defaultFinding: defaultFinding.trim(),
              options,
              isActive,
            })
          }
        >
          Save
        </button>
      </div>
    </div>
  );
}

export default function ClinicalTemplatesPage() {
  const { data, loading, error, refetch } =
    useQuery<VisitNoteClinicalTemplatesResult>(
      VISIT_NOTE_CLINICAL_TEMPLATES_QUERY,
    );

  const [save, { loading: saving }] = useMutation<
    SetVisitNoteClinicalTemplateResult,
    SetVisitNoteClinicalTemplateVars
  >(SET_VISIT_NOTE_CLINICAL_TEMPLATE_MUTATION);

  const systems = data?.visitNoteClinicalTemplates ?? [];

  // Both axes start collapsed: 24 systems expanded is a wall of text that
  // buries Physical exam entirely below the fold. The counts in the collapsed
  // headers keep the state legible without expanding anything.
  const [openAxes, setOpenAxes] = useState<string[]>([]);
  const [openSystems, setOpenSystems] = useState<string[]>([]);

  const toggleAxis = (key: string) =>
    setOpenAxes((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  const toggleSystem = (id: string) =>
    setOpenSystems((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );

  const onSave = async (next: VisitNoteClinicalTemplateSystem) => {
    try {
      await save({
        variables: {
          input: {
            id: next.id,
            label: next.label,
            defaultFinding: next.defaultFinding,
            options: next.options,
            sortOrder: next.sortOrder,
            isActive: next.isActive,
          },
        },
      });
      toast.success(`${next.label} saved`);
      await refetch();
    } catch (err) {
      // The backend's messages here are ours and already user-safe (a default
      // outside its options, an empty finding list, a removed row).
      toast.error(err instanceof Error ? err.message : "Save failed");
    }
  };

  return (
    <AdminWall error={error} screenLabel="Clinical Templates">
      <div className="space-y-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold">Clinical templates</h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              The review-of-systems and physical-exam dropdowns a physician edits
              while drafting a note. Nothing here is AI-generated.{" "}
              <strong className="font-semibold">
                These templates are not yet read by the note editor
              </strong>{" "}
              — see below.
            </p>
          </div>
          <button
            type="button"
            className="flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs"
            onClick={() => refetch()}
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </header>

        {/* The honest statement of what a save here does, next to the controls
            that do it. Every count and badge below describes THIS TABLE, not
            what a physician sees. */}
        <div
          data-testid="not-wired-banner"
          className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <strong className="font-semibold">Not live yet.</strong> The note
          editor does not read these templates — physicians still get the
          built-in review-of-systems and physical-exam defaults that ship in{" "}
          <code>@astrana/ui</code>. Saving here records the configuration for
          when the editor is wired to this table; it does not change what any
          physician sees today, and enabling or disabling a system does not add
          or remove it from anyone’s picker.
        </div>

        {loading && <LoadingSpinner />}

        {error && (
          <p className="text-sm text-red-600">
            Could not load the templates. Drafting is unaffected — the shipped
            defaults are used whenever this table cannot be read.
          </p>
        )}

        {!loading && !error && systems.length === 0 && (
          <p className="text-sm text-slate-600">
            No templates configured, so drafting uses the shipped defaults. Apply
            migration 0018 to customise them here.
          </p>
        )}

        {AXES.map(({ key, title }) => {
          const forAxis = systems.filter((s) => s.axis === key);
          if (forAxis.length === 0) return null;
          const axisOpen = openAxes.includes(key);
          const shownCount = forAxis.filter((s) => s.isActive).length;
          const axisBodyId = `axis-body-${key}`;
          return (
            <section
              key={key}
              className="rounded-lg border border-slate-200"
              data-testid={`axis-${key}`}
            >
              <button
                type="button"
                onClick={() => toggleAxis(key)}
                aria-expanded={axisOpen}
                aria-controls={axisBodyId}
                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
              >
                {axisOpen ? (
                  <ChevronDown size={16} className="shrink-0 text-slate-500" />
                ) : (
                  <ChevronRight size={16} className="shrink-0 text-slate-500" />
                )}
                {/* role/aria-level rather than a real <h2>: <button>'s content
                    model is phrasing content, so an <h2> in here is invalid HTML
                    and drops screen-reader heading navigation inside an
                    interactive control. */}
                <span
                  role="heading"
                  aria-level={2}
                  className="text-sm font-semibold text-slate-800"
                >
                  {title} — {forAxis.length} system{forAxis.length === 1 ? "" : "s"}
                </span>
                {/* "enabled/disabled" and not "shown/hidden": this flag is
                    configuration in this table, and nothing reads it yet. */}
                <span className="ml-auto text-xs text-slate-500">
                  {shownCount} enabled
                  {shownCount === forAxis.length
                    ? ""
                    : `, ${forAxis.length - shownCount} disabled`}
                </span>
              </button>

              <div
                id={axisBodyId}
                hidden={!axisOpen}
                className="space-y-2 border-t border-slate-200 p-3"
              >
                {forAxis.map((system) => (
                  <SystemRow
                    key={system.id}
                    system={system}
                    onSave={onSave}
                    saving={saving}
                    open={openSystems.includes(system.id)}
                    onToggle={() => toggleSystem(system.id)}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </AdminWall>
  );
}
